import React, { useEffect, useMemo, useState } from "react";
import { catalogApi, userApi } from "../../api/auctiongate.js";
import { Skel } from "./carHelpers.jsx";
import LogisticsTabs from "./LogisticsTabs.jsx";

/**
 * Delivery / customs calculator — full-width spec-block.
 *
 * Layout:
 *   ┌ title ───────────────────────────────────────────────┐
 *   │ INPUTS (left, grouped by section)  │ TOTAL (right)   │
 *   │  · Vehicle          year / fuel    │  big $          │
 *   │  · Auction          bid / fee      │  breakdown rows │
 *   │  · Customs          country / cost │  currency hint  │
 *   └─────────────────────────────────────┴─────────────────┘
 */
const VEHICLE_TYPES = [
  { v: "price", l: "Car" },
  { v: "price_pickup", l: "Pickup" },
  { v: "price_suv", l: "SUV" },
  { v: "price_van", l: "Van" },
  { v: "price_moto", l: "Moto" },
  { v: "price_travel_trailer", l: "Travel Trailer" },
];

const CUSTOM_COUNTRIES = [
  { v: "", l: "— none —" },
  { v: "Ukraine", l: "Ukraine" },
  { v: "Europe", l: "EU countries" },
];

// Sea-freight surcharges by vehicle type (per the official bidcars.ca rate sheet).
const VEHICLE_SURCHARGE = {
  price: 0,
  price_pickup: 0,
  price_suv: 150,
  price_van: 250, // oversize van (e.g. MB Sprinter 2500/3500)
  price_moto: 0,
  price_travel_trailer: 0,
};
const EV_SURCHARGE = 250; // electric / hybrid

// Optional services (bidcars.ca rate sheet).
const SERVICES = [
  { key: "title", label: "Title service", price: 160 },
  { key: "photo", label: "Extra photo / video", price: 180 },
  { key: "inspect", label: "Pre-load inspect + cleaning", price: 280 },
  { key: "lien", label: "Lien & loan release", price: 450 },
];
const isEvFuel = (fuel) => /electric|hybrid|\bev\b|plug|гибрид|электр/i.test(String(fuel || ""));

export default function CarCalculator({ bidcars, auctionKey, titleFee = 0 }) {
  const [fee, setFee] = useState(null);
  const [feeErr, setFeeErr] = useState(null);
  const [course, setCourse] = useState(null);
  const [landTable, setLandTable] = useState(null);

  const [vehicleType, setVehicleType] = useState("price");
  const [year, setYear] = useState(String(bidcars?.year ?? new Date().getFullYear()));
  const [fuel, setFuel] = useState(bidcars?.fuelType || bidcars?.fuel || "Gasoline");
  const [engine, setEngine] = useState(String(bidcars?.engineCapacity ?? bidcars?.engine ?? 2000));
  const [bid, setBid] = useState(String(bidcars?.currentBid ?? 1000));
  const [feeLevel, setFeeLevel] = useState(bidcars?.auctionFee === "low" ? "fee1low" : "fee1high");
  const [originKey, setOriginKey] = useState("");
  const [portKey, setPortKey] = useState("");
  // Active logistics tab: "port" | "base" | "europe". Drives total composition.
  const [tabMode, setTabMode] = useState("port");
  // Tab 3 source: "auction" | "warehouse" (which is the starting point for sea freight)
  const [fromMode, setFromMode] = useState("auction");
  const [seaPortKey, setSeaPortKey] = useState("");
  const [euCountry, setEuCountry] = useState("");
  const [customCountry, setCustomCountry] = useState("");
  const [customDelivery, setCustomDelivery] = useState("1000");
  const [services, setServices] = useState({});

  useEffect(() => {
    let cancelled = false;
    catalogApi.fee().then(
      (r) => !cancelled && setFee(r),
      (e) => !cancelled && setFeeErr(e?.message || "Failed")
    );
    userApi.course().then(
      (r) => !cancelled && setCourse(Number(r?.rate) || null),
      () => {}
    );
    catalogApi.landDelivery().then(
      (r) => !cancelled && setLandTable(r),
      () => {}
    );
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-pick origin from lot location once land table loads.
  useEffect(() => {
    if (!landTable || originKey) return;
    const loc = (bidcars?.locationName || bidcars?.location || "").toString().trim();
    if (!loc) return;
    const norm = (s) => s.toUpperCase().replace(/\s+/g, " ").trim();
    const target = norm(loc);
    const hit = landTable.origins?.find((o) => norm(o.name) === target);
    if (hit) setOriginKey(hit.name);
  }, [landTable, bidcars, originKey]);

  const auction = auctionKey?.toLowerCase().includes("iaai") ? "iaai" : "copart";

  // Lookup land delivery price (auction → port), markup already baked in.
  const landPrice = useMemo(() => {
    if (!landTable || !originKey || !portKey) return 0;
    const o = landTable.origins?.find((x) => x.name === originKey);
    return Number(o?.prices?.[portKey]) || 0;
  }, [landTable, originKey, portKey]);

  // Port → US warehouse (Beaver Dam, WI). Used when tabMode === "base".
  const warehousePrice = useMemo(() => {
    if (!landTable || tabMode !== "base" || !portKey) return 0;
    return Number(landTable.portToWarehouse?.[portKey]) || 0;
  }, [landTable, portKey, tabMode]);

  // RoRo sea freight: US/CA port → EU port. Used when tabMode === "europe".
  const seaPrice = useMemo(() => {
    if (!landTable || tabMode !== "europe" || !portKey || !seaPortKey) return 0;
    return Number(landTable.seaRates?.[portKey]?.[seaPortKey]) || 0;
  }, [landTable, portKey, seaPortKey, tabMode]);

  // EU last-mile: arrival port → country interior.
  const euLastMilePrice = useMemo(() => {
    if (!landTable || tabMode !== "europe" || !seaPortKey || !euCountry) return 0;
    return Number(landTable.euLastMile?.[seaPortKey]?.[euCountry]) || 0;
  }, [landTable, seaPortKey, euCountry, tabMode]);

  const calc = useMemo(() => {
    const amount = Number(bid);
    if (!Number.isFinite(amount) || amount <= 0 || !fee) return null;
    const table = fee?.[auction]?.[feeLevel];
    const row = Array.isArray(table) ? table.find((r) => Number(r.point) >= amount) : null;
    const auctionFeeAmount = row ? Number(row.price) : 0;
    // tab 3 with fromMode=warehouse: car is already at our base; no auction→port leg.
    const land = (tabMode === "europe" && fromMode === "warehouse") ? 0 : landPrice;
    const warehouse = warehousePrice;
    const sea = seaPrice;
    const euLastMile = euLastMilePrice;
    const truck = 0;
    const custom = Number(customDelivery) || 0;

    let excise = 0;
    let duty = 0;
    let vat = 0;
    if (customCountry === "Ukraine" && Number.isFinite(Number(year))) {
      const age = new Date().getFullYear() - Number(year);
      const engineL = (Number(engine) || 0) / 1000;
      excise = Math.round(50 * (age + 1) * (engineL || 1));
      duty = Math.round(amount * 0.1);
      vat = Math.round((amount + excise + duty) * 0.2);
    }

    const titleFeeAmt = Number(titleFee) || 0;

    // Surcharges (bidcars.ca rate sheet): vehicle type, EV/hybrid, optional services.
    const vehicleSurcharge = VEHICLE_SURCHARGE[vehicleType] || 0;
    const evSurcharge = isEvFuel(fuel) ? EV_SURCHARGE : 0;
    const activeServices = SERVICES.filter((s) => services[s.key]);
    const serviceSurcharge = activeServices.reduce((sum, s) => sum + s.price, 0);
    const surcharges = vehicleSurcharge + evSurcharge + serviceSurcharge;

    const total =
      amount + auctionFeeAmount + land + warehouse + sea + euLastMile + truck + custom +
      excise + duty + vat + titleFeeAmt + surcharges;
    return {
      amount, auctionFeeAmount, land, warehouse, sea, euLastMile, truck, custom,
      excise, duty, vat, titleFee: titleFeeAmt,
      vehicleSurcharge, evSurcharge, serviceSurcharge, surcharges, activeServices, total
    };
  }, [
    bid, fee, auction, feeLevel, customCountry, customDelivery, year, engine, titleFee,
    landPrice, warehousePrice, seaPrice, euLastMilePrice, tabMode, fromMode,
    vehicleType, fuel, services
  ]);

  // Cost composition (for the stacked bar + legend in the summary column).
  const composition = useMemo(() => {
    if (!calc || calc.total <= 0) return null;
    const buckets = [
      { key: "bid",      label: "Bid",          color: "#0284c7", value: calc.amount },
      { key: "fee",      label: "Auction fee",  color: "#0ea5e9", value: calc.auctionFeeAmount },
      { key: "title",    label: "Title fee",    color: "#8b5cf6", value: calc.titleFee },
      { key: "delivery", label: "Delivery",     color: "#10b981", value: calc.land + calc.warehouse + calc.sea + calc.euLastMile + calc.custom },
      { key: "surcharge",label: "Surcharges",   color: "#a855f7", value: calc.surcharges },
      { key: "customs",  label: "Customs",      color: "#f59e0b", value: calc.excise + calc.duty + calc.vat },
    ].filter((b) => b.value > 0);
    return buckets.map((b) => ({ ...b, pct: (b.value / calc.total) * 100 }));
  }, [calc]);

  // Buy Now comparison (if the lot has a Buy Now price).
  const buyNowAmount = Number(bidcars?.buyItNowPrice);
  const buyNowDelta = useMemo(() => {
    if (!Number.isFinite(buyNowAmount) || buyNowAmount <= 0 || !calc) return null;
    const delta = buyNowAmount - Number(bid);
    return { buyNow: buyNowAmount, delta };
  }, [buyNowAmount, calc, bid]);

  const loading = !fee && !feeErr;

  return (
    <div className="nd-car-detail__spec-block calc-block">
      <div className="calc-block__head">
        <h3 className="nd-car-detail__spec-block-title calc-block__title">Calculator</h3>
        <span className="small muted">Estimated landed cost</span>
      </div>

      {feeErr && <div className="login-error">{feeErr}</div>}

      <div className="calc-block__logistics-wrap">
        <LogisticsTabs
          landTable={landTable}
          bidcars={bidcars}
          tabMode={tabMode}
          onTabModeChange={(m) => {
            setTabMode(m);
            if (m !== "europe") {
              setSeaPortKey("");
              setEuCountry("");
            }
          }}
          originKey={originKey}
          onOriginChange={setOriginKey}
          portKey={portKey}
          onPortChange={setPortKey}
          seaPortKey={seaPortKey}
          onSeaPortChange={setSeaPortKey}
          euCountry={euCountry}
          onEuCountryChange={setEuCountry}
          fromMode={fromMode}
          onFromModeChange={setFromMode}
          landPrice={landPrice}
          warehousePrice={warehousePrice}
          seaPrice={seaPrice}
          euLastMilePrice={euLastMilePrice}
        />
      </div>

      <div className="calc-block__grid">
        {/* ── Inputs ───────────────────────────────────────────── */}
        <div className="calc-block__form">
          <FieldGroup title="Vehicle" cols={4}>
            <Field label="Type">
              <select
                className="calc-field__input"
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value)}
                disabled={loading}
              >
                {VEHICLE_TYPES.map((v) => (
                  <option key={v.v} value={v.v}>{v.l}</option>
                ))}
              </select>
            </Field>
            <Field label="Year">
              <input
                className="calc-field__input"
                value={year}
                maxLength={4}
                onChange={(e) => setYear(e.target.value)}
                disabled={loading}
              />
            </Field>
            <Field label="Fuel">
              <input
                className="calc-field__input"
                value={fuel}
                onChange={(e) => setFuel(e.target.value)}
                disabled={loading}
              />
            </Field>
            <Field label="Engine, cc">
              <input
                className="calc-field__input"
                value={engine}
                onChange={(e) => setEngine(e.target.value)}
                disabled={loading}
              />
            </Field>
          </FieldGroup>

          <FieldGroup title="Auction" cols={4}>
            <Field label="Winning bid, USD" span={2}>
              <input
                className="calc-field__input"
                type="number"
                value={bid}
                onChange={(e) => setBid(e.target.value)}
                disabled={loading}
              />
              {Number.isFinite(buyNowAmount) && buyNowAmount > 0 && (
                <button
                  type="button"
                  className="calc-field__shortcut"
                  onClick={() => setBid(String(Math.round(buyNowAmount)))}
                >
                  Use Buy Now (${formatMoney(buyNowAmount)})
                </button>
              )}
            </Field>
            <Field label="Fee tier" span={2}>
              <select
                className="calc-field__input"
                value={feeLevel}
                onChange={(e) => setFeeLevel(e.target.value)}
                disabled={loading}
              >
                <option value="fee1high">High fee</option>
                <option value="fee1low">Low fee</option>
                <option value="fee2">Tier 2</option>
              </select>
            </Field>
          </FieldGroup>

          <FieldGroup title="Customs" cols={4}>
            <Field label="Destination" span={2}>
              <select
                className="calc-field__input"
                value={customCountry}
                onChange={(e) => setCustomCountry(e.target.value)}
                disabled={loading}
              >
                {CUSTOM_COUNTRIES.map((c) => (
                  <option key={c.v} value={c.v}>{c.l}</option>
                ))}
              </select>
            </Field>
            <Field label="Delivery cost, USD" span={2}>
              <input
                className="calc-field__input"
                value={customDelivery}
                onChange={(e) => setCustomDelivery(e.target.value)}
                disabled={loading || !customCountry}
              />
            </Field>
          </FieldGroup>

          <div className="calc-field-group">
            <div className="calc-field-group__title">Surcharges &amp; services</div>
            <div className="calc-surcharges">
              {SERVICES.map((s) => {
                const on = !!services[s.key];
                return (
                  <label key={s.key} className={`calc-surcharge${on ? " is-on" : ""}`}>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={(e) => setServices((p) => ({ ...p, [s.key]: e.target.checked }))}
                      disabled={loading}
                    />
                    <span className="calc-surcharge__label">{s.label}</span>
                    <span className="calc-surcharge__price">+${s.price}</span>
                  </label>
                );
              })}
            </div>
            {(VEHICLE_SURCHARGE[vehicleType] > 0 || isEvFuel(fuel)) && (
              <div className="calc-surcharge-auto">
                Auto-applied:
                {VEHICLE_SURCHARGE[vehicleType] > 0 && (
                  <b> {VEHICLE_TYPES.find((v) => v.v === vehicleType)?.l} +${VEHICLE_SURCHARGE[vehicleType]}</b>
                )}
                {VEHICLE_SURCHARGE[vehicleType] > 0 && isEvFuel(fuel) && " ·"}
                {isEvFuel(fuel) && <b> Electric/Hybrid +${EV_SURCHARGE}</b>}
              </div>
            )}
          </div>

          <p className="small muted calc-block__hint">
            Auction → port prices include a ${landTable?.markupUsd ?? 200} markup over the carrier rate.
            Port → warehouse, sea (RoRo) and EU last-mile rates are <b>approximate market estimates</b>
            (based on competitor quotes); refine with a real quote before booking.
            Customs is a rough Ukraine estimate.
          </p>
        </div>

        {/* ── Summary ───────────────────────────────────────────── */}
        <aside className="calc-block__summary">
          <div className="calc-block__total-card">
            <div className="calc-block__total-label">Estimated total</div>
            <div className="calc-block__total-value">
              {calc ? `$${formatMoney(calc.total)}` : <Skel w={140} />}
            </div>
            <div className="calc-block__total-foot">
              {course && (
                <span>ECB EUR→USD rate <b>{Number(course).toFixed(4)}</b></span>
              )}
              {buyNowDelta && (
                <span className="calc-block__total-buynow">
                  Buy Now <b>${formatMoney(buyNowDelta.buyNow)}</b>
                  {buyNowDelta.delta > 0 && <> · +${formatMoney(buyNowDelta.delta)} vs your bid</>}
                </span>
              )}
            </div>
          </div>

          {/* Stacked composition bar */}
          <div className="calc-block__compo">
            <div className="calc-block__compo-head">
              <span>Cost composition</span>
              {calc && <b>${formatMoney(calc.total)}</b>}
            </div>
            <div className="calc-block__compo-bar" role="img" aria-label="Cost composition">
              {composition && composition.length > 0
                ? composition.map((b) => (
                    <span
                      key={b.key}
                      className="calc-block__compo-seg"
                      style={{ width: `${b.pct}%`, background: b.color }}
                      title={`${b.label}: $${formatMoney(b.value)} (${b.pct.toFixed(1)}%)`}
                    />
                  ))
                : <span className="calc-block__compo-seg calc-block__compo-seg--skel" style={{ width: "100%" }} />
              }
            </div>
            <div className="calc-block__compo-legend">
              {composition && composition.length > 0
                ? composition.map((b) => (
                    <div key={b.key} className="calc-block__compo-item">
                      <span className="calc-block__compo-dot" style={{ background: b.color }} />
                      <span className="calc-block__compo-name">{b.label}</span>
                      <span className="calc-block__compo-pct">{b.pct.toFixed(0)}%</span>
                    </div>
                  ))
                : (
                  <div className="calc-block__compo-item muted small">
                    Add a winning bid to see composition.
                  </div>
                )}
            </div>
          </div>

          <dl className="calc-block__breakdown">
            <Line label="Bid" value={calc ? formatMoney(calc.amount) : null} />
            <Line label="Auction fee" value={calc ? formatMoney(calc.auctionFeeAmount) : null} />
            <Line
              label="Delivery (land)"
              value={calc?.land ? formatMoney(calc.land) : "select origin & port"}
              muted={!calc?.land}
            />
            {tabMode === "base" && (
              <Line
                label="Port → warehouse (PA)"
                value={calc?.warehouse ? formatMoney(calc.warehouse) : "select port"}
                muted={!calc?.warehouse}
              />
            )}
            {tabMode === "europe" && (
              <>
                <Line
                  label="Sea (RoRo) port → EU"
                  value={calc?.sea ? formatMoney(calc.sea) : "select EU port"}
                  muted={!calc?.sea}
                />
                {(euCountry || calc?.euLastMile > 0) && (
                  <Line
                    label="EU last-mile"
                    value={calc?.euLastMile ? formatMoney(calc.euLastMile) : "—"}
                    muted={!calc?.euLastMile}
                  />
                )}
              </>
            )}
            <Line
              label="Delivery (custom)"
              value={calc?.custom ? formatMoney(calc.custom) : "—"}
              muted={!calc?.custom}
            />
            {calc?.titleFee > 0 && <Line label="Title fee" value={formatMoney(calc.titleFee)} />}
            {calc?.surcharges > 0 && (
              <>
                <Divider label="Surcharges & services" />
                {calc.vehicleSurcharge > 0 && (
                  <Line label={`${VEHICLE_TYPES.find((v) => v.v === vehicleType)?.l || "Vehicle"} surcharge`} value={formatMoney(calc.vehicleSurcharge)} />
                )}
                {calc.evSurcharge > 0 && <Line label="Electric / hybrid" value={formatMoney(calc.evSurcharge)} />}
                {calc.activeServices.map((s) => (
                  <Line key={s.key} label={s.label} value={formatMoney(s.price)} />
                ))}
              </>
            )}
            {customCountry === "Ukraine" && calc && (
              <>
                <Divider label="Customs (Ukraine)" />
                <Line label="Excise" value={formatMoney(calc.excise)} />
                <Line label="Duty (tax)" value={formatMoney(calc.duty)} />
                <Line label="VAT" value={formatMoney(calc.vat)} />
              </>
            )}
          </dl>
        </aside>
      </div>
    </div>
  );
}

// ─── Inner pieces ───────────────────────────────────────────────────
function FieldGroup({ title, cols = 2, children }) {
  return (
    <div className="calc-field-group">
      <div className="calc-field-group__title">{title}</div>
      <div className="calc-field-group__grid" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, span = 1, children }) {
  return (
    <label className="calc-field" style={span > 1 ? { gridColumn: `span ${span}` } : undefined}>
      <span className="calc-field__label">{label}</span>
      {children}
    </label>
  );
}

function Line({ label, value, muted }) {
  return (
    <div className={`calc-line${muted ? " calc-line--muted" : ""}`}>
      <dt>{label}</dt>
      <dd>
        {value == null
          ? <Skel w={64} />
          : typeof value === "number"
            ? `$${formatMoney(value)}`
            : (muted ? value : <>${String(value).startsWith("$") ? value.slice(1) : value}</>)}
      </dd>
    </div>
  );
}

function Divider({ label }) {
  return (
    <div className="calc-divider">
      <span>{label}</span>
    </div>
  );
}

function formatMoney(n) {
  if (!Number.isFinite(Number(n))) return "0";
  return Math.round(Number(n))
    .toString()
    .replace(/(\d)(?=(\d\d\d)+([^\d]|$))/g, "$1,");
}
